import { useTranslation } from 'react-i18next';
import { useEffect, useRef, useState } from 'react';

import BookingLoadingPlaceholder from './BookingLoadingPlaceholder';
import StripePortal from './StripePortal';
//services and global state
import { useGlobal } from '../../Core/GlobalState/Store';
import PaymentService from '../../Core/Services/PaymentService';
import FormLayout from '../ReusableComponents/FormLayout';
import SettingsService from '../../Core/Services/SettingsService';
import BookingSummary from '../ReusableComponents/BookingSummary';

const Checkout = () => {
  const { t } = useTranslation();

  const {
    user,
    loading,
    setLoading,
    date,
    currentLocation,
    orderId,
    selectedSize,
  } = useGlobal();

  const [total, setTotal] = useState(0);
  const [error, setError] = useState(null);
  const [options, setOptions] = useState(null);
  const hasFetched = useRef(false);

  useEffect(() => {
    if (hasFetched.current) return;
    hasFetched.current = true;

    const fetchCheckoutSession = async () => {
      setLoading(true);
      setError(null);

      try {
        const result = await PaymentService.createCheckoutSession({
          orderId,
          user,
          date,
          currentLocation,
        });

        if (!result || result === false) {
          throw new Error('Failed to create checkout session');
        }

        const {
          clientSecret,
          customerSessionClientSecret,
          totalPaymentAmount,
        } = result;

        setTotal(totalPaymentAmount);
        setOptions({
          clientSecret,
          customerSessionClientSecret,
        });

        // we push the google analytics event here, because we want to make sure the checkout session is created before we log the event. This way, we can be sure that the user has reached the point of actually paying, and not just clicking through the checkout process.
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
          event: 'form_submission_self_storage_booking',
          location: currentLocation.name,
          email: user?.email,
          size: selectedSize?.title,
          value: totalPaymentAmount,
          currency: SettingsService.getCurrency(),
        });
      } catch (e) {
        setError(t(e.message || 'error_create_order_tryagain'));
      } finally {
        setLoading(false);
      }
    };

    fetchCheckoutSession();
  }, [
    user,
    date,
    currentLocation,
    orderId,
    selectedSize,
    setLoading,
    t,
  ]);

  if (error) {
    return (
      <div className="w-full p-8 text-center">
        <div className="text-red-600 font-bold text-xl mb-4">
          {t('error_title') || 'Error'}
        </div>
        <div className="text-gray-700 mb-4">{error}</div>
      </div>
    );
  }

  if (loading) {
    return <BookingLoadingPlaceholder />;
  }

  if (!options?.clientSecret) {
    return (
      <div className="w-full p-8 text-center">
        <div className="text-red-600 font-bold text-xl mb-4">
          {t('error_title') || 'Error'}
        </div>
        <div className="text-gray-700 mb-4">
          {t('error_create_order_tryagain')}
        </div>
      </div>
    );
  }

  return (
    <FormLayout title={t('choose_payment_method_title')}>
      <div className="md:grid grid-cols-1 flex flex-col-reverse gap-6 md:grid-cols-12 !border !border-solid border-gray-400">
        <div className="col-span-12 md:col-span-7 p-4 md:min-h-[800px]">
          <StripePortal options={options} />
        </div>
        <div className="col-span-12 md:col-span-5 ">
          <BookingSummary firstMonthPrice={total} />
        </div>
      </div>
    </FormLayout>
  );
};

export default Checkout;
